/**
 * The box both comparison charts are drawn in (SPEC-008 PR-8.5).
 *
 * One module rather than two copies, because this screen draws two plots that
 * stack and they have to be the same size to the unit — a chart that was four
 * units narrower than the one above it reads as indented rather than as a second
 * chart. It is also the repo's rule about not having two ways to do an
 * established thing, applied at the smallest scale it applies at.
 *
 * The single-car charts on the detail card keep their own geometry for now and
 * that is a stated deferral rather than an oversight. Those two are mid-change
 * under SPEC-002's and SPEC-004's own slices; folding three sheets into one
 * while two of them are moving is how a merge loses a decision that was argued
 * for. The day both are still, this is the module they collapse into — the
 * numbers below are already theirs.
 *
 * No React and no `t()`: this is arithmetic and a set of constants.
 *
 * ## THE FRAME IS A PARAMETER, NOT A CONSTANT
 *
 * It used to be a module constant, `VIEW = { width: 320, height: 200 }`, and
 * that was correct for exactly as long as this product was 640px wide. A viewBox
 * scales with its box: released to a 1100px column the same 320 units magnify to
 * 3.4x, which turns 9px tick figures into 30px ones and 2px strokes into 7px —
 * a POSTER OF A CHART rather than a chart, and one that grows proportionally
 * taller as it does it. Nothing fails; it just stops being an instrument.
 *
 * The fix is not to edit the number, which would take the phone with it. It is
 * to have two frames and pass one in, so a caller says which composition it is
 * drawing and every projector, axis and inset follows. `HEADROOM` and
 * `SERIES_CLASS` stay module-wide because they are decisions about the DATA
 * rather than about the box.
 */

/**
 * A frame, and everything the renderers derive from it.
 *
 * Derived once here rather than recomputed at each call site, because the four
 * quantities below are the ones two components used to spell out inline —
 * `VIEW.width - PLOT.right` appeared eleven times across the two charts, and
 * eleven copies of an arithmetic step is eleven places for a second frame to be
 * half-applied.
 */
function chartFrame({ view, plot }) {
    const width = view.width - plot.left - plot.right;
    const height = view.height - plot.top - plot.bottom;

    return {
        view,
        plot,
        width,
        height,
        /** The plot's far edge and its floor, which is where the axes run. */
        right: view.width - plot.right,
        floor: plot.top + height,
        viewBox: `0 0 ${view.width} ${view.height}`,
        /** The frame a reader measures against: the two axes the origin is a corner of. */
        axisPath: [
            `M${plot.left} ${plot.top}`,
            `L${plot.left} ${plot.top + height}`,
            `L${view.width - plot.right} ${plot.top + height}`,
        ].join(' '),
    };
}

/**
 * The phone's frame, unchanged to the unit.
 *
 * Taller than the single-car charts by twenty units, and the reason is the
 * second series. Two curves that cross need vertical room to be told apart at
 * the crossing, and a legend has to sit somewhere that is not on top of a line.
 *
 * `left` is wide enough for money — `$1.5M` — because the two charts share this
 * box and the wider requirement wins. The alternative is a value chart indented
 * from the speed chart above it, which is exactly what this module exists to
 * prevent. `top` seats a legend rather than just the topmost mark.
 */
export const NARROW_FRAME = chartFrame({
    view: { width: 320, height: 200 },
    plot: { left: 40, right: 10, top: 22, bottom: 24 },
});

/**
 * The desktop's frame: WIDER, and barely taller.
 *
 * 640x240 rather than a scaled-up 320x200, and the change of PROPORTION is the
 * whole point. Speed against time and money against year are both landscape
 * facts — the interesting thing about either is how the curve travels sideways
 * — and a square-ish box released onto a desktop spends its new pixels growing
 * downwards, which is the one direction that costs a reader a scroll.
 *
 * The insets grow a little rather than proportionally: they exist to seat tick
 * figures, and the tick figures are set in the SAME user units in both frames
 * (`compareChart.css`), so their footprint is a constant rather than a fraction.
 * Two units of extra `left` covers the wider money labels a taller money axis
 * can produce; four of extra `bottom` seats the year row off the floor.
 *
 * The number is chosen against the column it is drawn in rather than against a
 * screen: `compare.css` gives a chart plate between about 700 and 1100px above
 * 1024, so 640 units render at 1.1x to 1.7x. That is the band where a 9px tick
 * figure reads as 10-15px and a 2px stroke as 2-3px — the weight they were
 * drawn at.
 */
export const WIDE_FRAME = chartFrame({
    view: { width: 640, height: 240 },
    plot: { left: 48, right: 16, top: 24, bottom: 28 },
});

/** Which frame a composition is drawing in. One call, so nothing branches twice. */
export function compareFrame(wide) {
    return wide ? WIDE_FRAME : NARROW_FRAME;
}

/**
 * How far past the data the axes run.
 *
 * Scaled exactly to the data, the extreme point lands ON the frame and its mark
 * is drawn half outside the plot — so the one point that says how far a car
 * actually went is the one point that reads as clipped. The tick labels stay
 * keyed to the real extent, so nothing here invents a gridline.
 */
export const HEADROOM = 1.08;

/** The two lanes, as classes, so nothing downstream decides which car is which colour. */
export const SERIES_CLASS = ['compare-chart__series--a', 'compare-chart__series--b'];

/**
 * A linear projector over a box that starts at zero.
 *
 * Both axes of the acceleration chart and the year axis of the value chart. The
 * y axis flips, because a viewBox counts downwards and a quantity does not.
 */
export function linearProjector(frame, { width, height }) {
    return {
        x: (value) => frame.plot.left + (value / width) * frame.width,
        y: (value) => frame.plot.top + frame.height - (value / height) * frame.height,
    };
}

/**
 * A logarithmic projector, for money (D-20).
 *
 * A $3.05M F40 and a $37K Esprit on one linear axis draw the Esprit as a flat
 * line along the floor — a chart that has hidden one of its two subjects, which
 * is a worse failure than any scale can be. On a log axis equal vertical
 * distances are equal RATIOS, which is the genuinely comparable thing about two
 * assets, and the gridlines still carry real money so nothing is concealed.
 *
 * It is used for money and nowhere else. Speed and time in this catalogue span a
 * factor of two, and a log axis there would be an affectation that made an easy
 * chart hard.
 *
 * The floor is a decade below the smallest value rather than zero, because zero
 * has no logarithm and no car in this catalogue has ever been worth nothing.
 */
export function logProjector(frame, { width, floor, ceiling }) {
    const low = Math.log10(floor);
    const span = Math.log10(ceiling) - low;

    return {
        x: (value) => frame.plot.left + (value / width) * frame.width,
        y: (value) => (span <= 0
            ? frame.plot.top + frame.height / 2
            : frame.floor - ((Math.log10(Math.max(value, floor)) - low) / span) * frame.height),
    };
}

/** A series as one `d`, with no smoothing this module is entitled to invent. */
export function polyline(points, project) {
    return points
        .map((point, index) => `${index === 0 ? 'M' : 'L'}${project.x(point.x).toFixed(2)} ${project.y(point.y).toFixed(2)}`)
        .join(' ');
}
